import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, X, Star, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api, { auth as authApi, billing as billingApi, stripe as stripeApi } from '../utils/api';
import { eAppNativa, voceRegistratoreVisibile } from '../utils/appNativa';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

function Section({ id, title, description, children }) {
  return (
    <div id={id} style={{ scrollMarginTop: 16, background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', boxShadow: CARD_SHADOW, marginBottom: 24, overflow: 'hidden' }}>
      <div style={{ padding: '20px 28px', borderBottom: '1px solid var(--ds-border)' }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>{title}</h2>
        {description && <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ds-muted)' }}>{description}</p>}
      </div>
      <div style={{ padding: '24px 28px' }}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</label>
      {children}
    </div>
  );
}

const inp = { width: '100%', padding: '10px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', color: 'var(--ds-text)' };

function SaveBtn({ loading, label = 'Salva modifiche' }) {
  return (
    <button type="submit" disabled={loading} style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
      {loading ? 'Salvataggio…' : label}
    </button>
  );
}

function Alert({ type, msg, onClose }) {
  if (!msg) return null;
  const colors = { success: { bg: tint('var(--ok)', 12), border: 'var(--ok)', color: 'var(--ok)' }, error: { bg: tint('var(--ds-danger)', 10), border: 'var(--ds-danger)', color: 'var(--ds-danger)' } };
  const c = colors[type] || colors.success;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 'var(--radius-base)', background: c.bg, border: `1px solid ${c.border}`, color: c.color, fontSize: 14, marginBottom: 16 }}>
      <span style={{ flex: 1 }}>{msg}</span>
      <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.color, display: 'flex' }}><X size={16} strokeWidth={2} /></button>
    </div>
  );
}

// ── Sezione Profilo ───────────────────────────────────────────────────────
// SEC-051-profilo (DEC-069): il numero WhatsApp NON è più un campo di testo del
// profilo — instrada i vocali in arrivo, quindi va PROVATO col possesso (codice
// SMS) prima di associarlo. Il salvataggio del profilo tocca solo nome/cognome;
// il numero ha un flusso a parte: view → edit (invia codice) → otp (conferma).
const MSG_PHONE = {
  telefono_non_valido: 'Il numero non è valido. Usa il formato internazionale (es. +39 333 1234567).',
  telefono_invariato: 'Questo è già il tuo numero.',
  telefono_gia_registrato: 'Questo numero è già associato a un altro account.',
  otp_cooldown: 'Hai già richiesto un codice da poco. Attendi un minuto.',
  sms_non_configurato: 'Il servizio di invio SMS non è al momento disponibile. Riprova più tardi.',
  sms_non_inviato: 'Non siamo riusciti a inviare l\'SMS a questo numero. Controlla che sia corretto e riprova tra qualche minuto.',
  otp_non_valido: 'Codice non valido o scaduto. Richiedine uno nuovo.',
  dati_mancanti: 'Inserisci numero e codice.',
};

function ProfiloSection({ user, onUpdate }) {
  const [form, setForm] = useState({ nome: user?.nome || '', cognome: user?.cognome || '' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // Cambio numero verificato: 'view' | 'edit' | 'otp'
  const [numeroMode, setNumeroMode] = useState('view');
  const [nuovoNumero, setNuovoNumero] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState(null);

  const salva = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.updateMe(form);
      onUpdate(form);
      setMsg({ type: 'success', text: 'Profilo aggiornato con successo' });
    } catch {
      setMsg({ type: 'error', text: 'Errore nel salvataggio del profilo' });
    } finally { setLoading(false); }
  };

  const phoneErr = (err, fallback) => {
    const codice = err.response?.data?.codice;
    setPhoneMsg({ type: 'error', text: MSG_PHONE[codice] || err.response?.data?.error || fallback });
  };

  const iniziaCambio = () => {
    setNumeroMode('edit');
    setNuovoNumero('');
    setOtpCode('');
    setPhoneMsg(null);
  };

  const annullaCambio = () => {
    setNumeroMode('view');
    setPhoneMsg(null);
  };

  const inviaCodice = async () => {
    setPhoneMsg(null);
    if (!nuovoNumero.trim()) { setPhoneMsg({ type: 'error', text: 'Inserisci il nuovo numero.' }); return; }
    setPhoneLoading(true);
    try {
      await authApi.mePhoneSendOtp(nuovoNumero.trim());
      setNumeroMode('otp');
      setOtpCode('');
    } catch (err) {
      phoneErr(err, 'Non siamo riusciti a inviare il codice. Riprova.');
    } finally { setPhoneLoading(false); }
  };

  const confermaCodice = async () => {
    setPhoneMsg(null);
    if (!otpCode.trim()) { setPhoneMsg({ type: 'error', text: 'Inserisci il codice ricevuto via SMS.' }); return; }
    setPhoneLoading(true);
    try {
      const { data } = await authApi.mePhoneConfirm({ telefono: nuovoNumero.trim(), otp_code: otpCode.trim() });
      onUpdate({ whatsapp_phone: data.whatsapp_phone });
      setNumeroMode('view');
      setPhoneMsg({ type: 'success', text: 'Numero verificato e aggiornato.' });
    } catch (err) {
      phoneErr(err, 'Codice non valido. Riprova.');
    } finally { setPhoneLoading(false); }
  };

  const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--brand)' };

  return (
    <Section title="Profilo" description="Il tuo nome viene mostrato nelle fatture e nei documenti.">
      <Alert type={msg?.type} msg={msg?.text} onClose={() => setMsg(null)} />
      <form onSubmit={salva}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Nome">
            <input style={inp} value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} required />
          </Field>
          <Field label="Cognome">
            <input style={inp} value={form.cognome} onChange={(e) => setForm((f) => ({ ...f, cognome: e.target.value }))} />
          </Field>
        </div>
        <Field label="Email">
          <input style={{ ...inp, background: 'var(--chip)', color: 'var(--ds-muted)' }} value={user?.email || ''} disabled />
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 4 }}>L'email non può essere modificata da qui.</div>
        </Field>
        <Field label="Azienda">
          <input style={{ ...inp, background: 'var(--chip)', color: 'var(--ds-muted)' }} value={user?.tenant?.nome || ''} disabled />
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 4 }}>Modifica il nome nella sezione "Dati azienda" qui sotto.</div>
        </Field>
        <SaveBtn loading={loading} />
      </form>

      {/* Numero WhatsApp — cambio VERIFICATO via SMS (SEC-051-profilo). Fuori
          dal form del profilo: ha il suo flusso (invia codice → conferma). */}
      <div style={{ borderTop: '1px solid var(--ds-border)', marginTop: 24, paddingTop: 20 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Numero WhatsApp</label>
        <Alert type={phoneMsg?.type} msg={phoneMsg?.text} onClose={() => setPhoneMsg(null)} />

        {numeroMode === 'view' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: user?.whatsapp_phone ? 'var(--ds-text)' : 'var(--ds-muted)' }}>
              {user?.whatsapp_phone || 'Nessun numero impostato'}
            </span>
            <button type="button" onClick={iniziaCambio} style={linkBtn}>
              {user?.whatsapp_phone ? 'Cambia numero' : 'Aggiungi numero'}
            </button>
          </div>
        )}

        {numeroMode === 'edit' && (
          <div>
            <input style={inp} value={nuovoNumero} onChange={(e) => setNuovoNumero(e.target.value)} placeholder="+39 333 1234567" inputMode="tel" />
            <div style={{ fontSize: 12, color: 'var(--ds-muted)', margin: '6px 0 12px' }}>
              Ti invieremo un codice via SMS per verificare che il numero è tuo.
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button type="button" onClick={inviaCodice} disabled={phoneLoading} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: phoneLoading ? 'wait' : 'pointer', fontSize: 14, fontWeight: 500 }}>
                {phoneLoading ? 'Invio…' : 'Invia codice SMS'}
              </button>
              <button type="button" onClick={annullaCambio} style={{ ...linkBtn, color: 'var(--ds-muted)' }}>Annulla</button>
            </div>
          </div>
        )}

        {numeroMode === 'otp' && (
          <div>
            <div style={{ fontSize: 13, color: 'var(--ds-text)', marginBottom: 10 }}>
              Codice inviato al <strong>{nuovoNumero.trim()}</strong>. Inseriscilo qui sotto.
            </div>
            <input
              style={{ ...inp, letterSpacing: '0.3em', textAlign: 'center', fontSize: 16 }}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="––––––" inputMode="numeric" autoComplete="one-time-code"
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
              <button type="button" onClick={confermaCodice} disabled={phoneLoading} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: phoneLoading ? 'wait' : 'pointer', fontSize: 14, fontWeight: 500 }}>
                {phoneLoading ? 'Verifica…' : 'Conferma numero'}
              </button>
              <button type="button" onClick={() => { setNumeroMode('edit'); setPhoneMsg(null); }} style={{ ...linkBtn, color: 'var(--ds-muted)' }}>Cambia numero</button>
            </div>
          </div>
        )}

        <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 10 }}>Necessario per l'assistente vocale AI.</div>
      </div>
    </Section>
  );
}

// ── Sezione Dati Azienda ─────────────────────────────────────────────────
const PROVINCE = [
  '','AG','AL','AN','AO','AP','AQ','AR','AT','AV','BA','BG','BI','BL','BN','BO',
  'BR','BS','BT','BZ','CA','CB','CE','CH','CL','CN','CO','CR','CS','CT','CZ','EN',
  'FC','FE','FG','FI','FM','FR','GE','GO','GR','IM','IS','KR','LC','LE','LI','LO',
  'LT','LU','MB','MC','ME','MI','MN','MO','MS','MT','NA','NO','NU','OR','PA','PC',
  'PD','PE','PG','PI','PN','PO','PR','PT','PU','PV','PZ','RA','RC','RE','RG','RI',
  'RM','RN','RO','SA','SI','SO','SP','SR','SS','SU','SV','TA','TE','TN','TO','TP',
  'TR','TS','TV','UD','VA','VB','VC','VE','VI','VR','VT','VV',
];

const PROVINCIA_TO_REGIONE = {
  'BG':'Lombardia','BS':'Lombardia','CO':'Lombardia','CR':'Lombardia','LC':'Lombardia',
  'LO':'Lombardia','MN':'Lombardia','MI':'Lombardia','MB':'Lombardia','PV':'Lombardia',
  'SO':'Lombardia','VA':'Lombardia',
  'RM':'Lazio','FR':'Lazio','LT':'Lazio','RI':'Lazio','VT':'Lazio',
  'NA':'Campania','AV':'Campania','BN':'Campania','CE':'Campania','SA':'Campania',
  'VE':'Veneto','BL':'Veneto','PD':'Veneto','RO':'Veneto','TV':'Veneto','VR':'Veneto','VI':'Veneto',
  'BO':'Emilia-Romagna','FE':'Emilia-Romagna','FC':'Emilia-Romagna','MO':'Emilia-Romagna',
  'PR':'Emilia-Romagna','PC':'Emilia-Romagna','RA':'Emilia-Romagna','RE':'Emilia-Romagna','RN':'Emilia-Romagna',
  'TO':'Piemonte','AL':'Piemonte','AT':'Piemonte','BI':'Piemonte','CN':'Piemonte',
  'NO':'Piemonte','VB':'Piemonte','VC':'Piemonte',
  'PA':'Sicilia','AG':'Sicilia','CL':'Sicilia','CT':'Sicilia','EN':'Sicilia',
  'ME':'Sicilia','RG':'Sicilia','SR':'Sicilia','TP':'Sicilia',
  'FI':'Toscana','AR':'Toscana','GR':'Toscana','LI':'Toscana','LU':'Toscana',
  'MS':'Toscana','PI':'Toscana','PO':'Toscana','PT':'Toscana','SI':'Toscana',
  'BA':'Puglia','BR':'Puglia','BT':'Puglia','FG':'Puglia','LE':'Puglia','TA':'Puglia',
  'CS':'Calabria','CZ':'Calabria','KR':'Calabria','RC':'Calabria','VV':'Calabria',
  'CA':'Sardegna','NU':'Sardegna','OR':'Sardegna','SS':'Sardegna','SU':'Sardegna',
  'GE':'Liguria','IM':'Liguria','SP':'Liguria','SV':'Liguria',
  'AN':'Marche','AP':'Marche','FM':'Marche','MC':'Marche','PU':'Marche',
  'AQ':'Abruzzo','CH':'Abruzzo','PE':'Abruzzo','TE':'Abruzzo',
  'TS':'Friuli-Venezia Giulia','GO':'Friuli-Venezia Giulia','PN':'Friuli-Venezia Giulia','UD':'Friuli-Venezia Giulia',
  'TN':'Trentino-Alto Adige','BZ':'Trentino-Alto Adige',
  'PG':'Umbria','TR':'Umbria',
  'PZ':'Basilicata','MT':'Basilicata',
  'CB':'Molise','IS':'Molise',
  'AO':"Valle d'Aosta",
};

const EMPTY_DATI = {
  nome: '', piva: '', codice_fiscale: '', codice_ateco: '', descrizione_attivita: '',
  indirizzo: '', citta: '', cap: '', provincia: '',
  telefono: '', email_fatturazione: '', email_commercialista: '',
  iban: '', bic: '', note_piede_pagina: '',
  firma_tipo: 'nessuna', firma_testo: '',
};

function datiFromTenant(t) {
  return {
    nome: t.nome || '', piva: t.piva || '', codice_fiscale: t.codice_fiscale || '',
    codice_ateco: t.codice_ateco || '', descrizione_attivita: t.descrizione_attivita || '',
    indirizzo: t.indirizzo || '', citta: t.citta || '', cap: t.cap || '', provincia: t.provincia || '',
    telefono: t.telefono || '', email_fatturazione: t.email_fatturazione || '',
    email_commercialista: t.email_commercialista || '',
    iban: t.iban || '', bic: t.bic || '', note_piede_pagina: t.note_piede_pagina || '',
    firma_tipo: t.firma_tipo || 'nessuna', firma_testo: t.firma_testo || '',
  };
}

function DatiAziendaSection({ user, onUpdate }) {
  const t = user?.tenant || {};
  const [form, setForm] = useState(t.nome !== undefined ? datiFromTenant(t) : EMPTY_DATI);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [logoUrl, setLogoUrl] = useState(t.logo_url || '');
  const [logoLoading, setLogoLoading] = useState(false);
  const [firmaUrl, setFirmaUrl] = useState(t.firma_url || '');
  const [firmaLoading, setFirmaLoading] = useState(false);

  useEffect(() => {
    const nt = user?.tenant || {};
    setForm(datiFromTenant(nt));
    setLogoUrl(nt.logo_url || '');
    setFirmaUrl(nt.firma_url || '');
  }, [user?.tenant]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const regione = PROVINCIA_TO_REGIONE[form.provincia] || '';

  const salva = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await authApi.updateTenant(form);
      onUpdate({ tenant: { ...(user?.tenant || {}), ...data } });
      setMsg({ type: 'success', text: 'Dati azienda aggiornati con successo' });
    } catch (err) {
      const errData = err.response?.data;
      const text = (typeof errData === 'object' && errData !== null)
        ? Object.values(errData).flat().join(' ')
        : 'Errore nel salvataggio dei dati azienda';
      setMsg({ type: 'error', text });
    } finally { setLoading(false); }
  };

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoLoading(true);
    setMsg(null);
    try {
      const { data } = await authApi.updateTenantLogo(file);
      setLogoUrl(data.logo_url || '');
      onUpdate({ tenant: { ...(user?.tenant || {}), ...data } });
      setMsg({ type: 'success', text: 'Logo aggiornato' });
    } catch (err) {
      const text = err.response?.data?.error || 'Errore nel caricamento del logo';
      setMsg({ type: 'error', text });
    } finally { setLogoLoading(false); e.target.value = ''; }
  };

  const onFirmaChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFirmaLoading(true);
    setMsg(null);
    try {
      const { data } = await authApi.updateTenantFirma(file);
      setFirmaUrl(data.firma_url || '');
      onUpdate({ tenant: { ...(user?.tenant || {}), ...data } });
      setMsg({ type: 'success', text: 'Firma aggiornata' });
    } catch (err) {
      const text = err.response?.data?.error || 'Errore nel caricamento della firma';
      setMsg({ type: 'error', text });
    } finally { setFirmaLoading(false); e.target.value = ''; }
  };

  const separatorStyle = { borderTop: '1px solid var(--ds-border)', marginTop: 20, paddingTop: 20, marginBottom: 4 };
  const subheadingStyle = { fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 12 };

  return (
    <Section title="Dati azienda" description="Questi dati vengono usati per fatture, preventivi e documenti.">
      <Alert type={msg?.type} msg={msg?.text} onClose={() => setMsg(null)} />
      <form onSubmit={salva}>
        <Field label="Nome azienda">
          <input style={inp} value={form.nome} onChange={(e) => set('nome', e.target.value)} required />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Partita IVA">
            <input style={inp} value={form.piva} onChange={(e) => set('piva', e.target.value)} placeholder="01234567890" maxLength={20} />
          </Field>
          <Field label="Codice Fiscale">
            <input style={inp} value={form.codice_fiscale} onChange={(e) => set('codice_fiscale', e.target.value)} placeholder="RSSMRA80A01H501U" maxLength={16} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Codice ATECO">
            <input style={inp} value={form.codice_ateco} onChange={(e) => set('codice_ateco', e.target.value)} placeholder="43.21.01" maxLength={10} />
          </Field>
          <Field label="Descrizione attività">
            <input style={inp} value={form.descrizione_attivita} onChange={(e) => set('descrizione_attivita', e.target.value)} placeholder="Es. Impianti elettrici" maxLength={200} />
          </Field>
        </div>

        <div style={separatorStyle}>
          <div style={subheadingStyle}>Sede</div>
        </div>
        <Field label="Indirizzo">
          <input style={inp} value={form.indirizzo} onChange={(e) => set('indirizzo', e.target.value)} placeholder="Via Roma 1" maxLength={300} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16 }}>
          <Field label="Città">
            <input style={inp} value={form.citta} onChange={(e) => set('citta', e.target.value)} maxLength={100} />
          </Field>
          <Field label="CAP">
            <input style={inp} value={form.cap} onChange={(e) => set('cap', e.target.value)} placeholder="00100" maxLength={10} />
          </Field>
          <Field label="Provincia">
            <select style={inp} value={form.provincia} onChange={(e) => set('provincia', e.target.value)}>
              {PROVINCE.map((p) => <option key={p} value={p}>{p || '— Seleziona —'}</option>)}
            </select>
          </Field>
        </div>
        {regione && (
          <Field label="Regione">
            <input style={{ ...inp, background: 'var(--chip)', color: 'var(--ds-muted)' }} value={regione} disabled />
          </Field>
        )}

        <div style={separatorStyle}>
          <div style={subheadingStyle}>Contatti</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Telefono">
            <input style={inp} type="tel" value={form.telefono} onChange={(e) => set('telefono', e.target.value)} placeholder="+39 06 1234567" maxLength={20} />
          </Field>
          <Field label="Email fatturazione">
            <input style={inp} type="email" value={form.email_fatturazione} onChange={(e) => set('email_fatturazione', e.target.value)} placeholder="fatturazione@azienda.it" />
          </Field>
        </div>
        <Field label="Email commercialista">
          <input style={inp} type="email" value={form.email_commercialista} onChange={(e) => set('email_commercialista', e.target.value)} placeholder="commercialista@studio.it" />
        </Field>

        <div style={separatorStyle}>
          <div style={subheadingStyle}>Dati bancari</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
          <Field label="IBAN">
            <input style={inp} value={form.iban} onChange={(e) => set('iban', e.target.value)} placeholder="IT60X0542811101000000123456" maxLength={34} />
          </Field>
          <Field label="BIC / SWIFT">
            <input style={inp} value={form.bic} onChange={(e) => set('bic', e.target.value)} placeholder="UNCRITM1XXX" maxLength={11} />
          </Field>
        </div>

        <div style={separatorStyle}>
          <div style={subheadingStyle}>Documenti</div>
        </div>
        <Field label="Note piè di pagina">
          <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={form.note_piede_pagina} onChange={(e) => set('note_piede_pagina', e.target.value)} placeholder="Es. Pagamento a 30 giorni data fattura. Coordinate bancarie sopra indicate." />
        </Field>
        <Field label="Logo aziendale">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {logoUrl
              ? <img src={logoUrl} alt="Logo azienda" style={{ height: 56, maxWidth: 160, objectFit: 'contain', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', padding: 4, background: '#fff' }} />
              : <div style={{ height: 56, width: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--ds-border)', borderRadius: 'var(--radius-base)', fontSize: 11, color: 'var(--ds-muted)' }}>Nessun logo</div>}
            <label style={{ padding: '8px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', cursor: logoLoading ? 'wait' : 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--ds-text)', background: 'var(--chip)' }}>
              {logoLoading ? 'Caricamento…' : 'Carica logo'}
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogoChange} disabled={logoLoading} style={{ display: 'none' }} />
            </label>
            <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>PNG, JPG o WEBP · max 5MB</span>
          </div>
        </Field>

        <div style={separatorStyle}>
          <div style={subheadingStyle}>Firma impresa</div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--ds-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Compare nel blocco "Firma dell'impresa" in calce ai preventivi inviati al cliente per accettazione.
        </p>
        <Field label="Tipo firma">
          <select style={inp} value={form.firma_tipo} onChange={(e) => set('firma_tipo', e.target.value)}>
            <option value="nessuna">Nessuna (spazio per firma a mano)</option>
            <option value="testo">Testo (nome / ragione sociale)</option>
            <option value="immagine">Immagine caricata</option>
          </select>
        </Field>
        {form.firma_tipo === 'testo' && (
          <Field label="Testo firma">
            <input style={inp} value={form.firma_testo} onChange={(e) => set('firma_testo', e.target.value)} placeholder="Es. Idraulico Demo — Mario Rossi" maxLength={120} />
          </Field>
        )}
        {form.firma_tipo === 'immagine' && (
          <Field label="Immagine firma">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {firmaUrl
                ? <img src={firmaUrl} alt="Firma impresa" style={{ height: 56, maxWidth: 200, objectFit: 'contain', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', padding: 4, background: '#fff' }} />
                : <div style={{ height: 56, width: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--ds-border)', borderRadius: 'var(--radius-base)', fontSize: 11, color: 'var(--ds-muted)' }}>Nessuna firma</div>}
              <label style={{ padding: '8px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', cursor: firmaLoading ? 'wait' : 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--ds-text)', background: 'var(--chip)' }}>
                {firmaLoading ? 'Caricamento…' : 'Carica firma'}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onFirmaChange} disabled={firmaLoading} style={{ display: 'none' }} />
              </label>
              <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>PNG, JPG o WEBP · max 5MB</span>
            </div>
          </Field>
        )}

        <SaveBtn loading={loading} label="Salva dati azienda" />
      </form>
    </Section>
  );
}

// ── Sezione Configurazione email (SMTP artigiano) ───────────────────────────
function ConfigurazioneEmailSection({ user, onUpdate }) {
  const t = user?.tenant || {};
  const [form, setForm] = useState({
    smtp_host: t.smtp_host || '', smtp_port: t.smtp_port || 587,
    smtp_user: t.smtp_user || '', smtp_password: '', smtp_use_tls: t.smtp_use_tls ?? true,
  });
  const [configured, setConfigured] = useState(!!t.smtp_configurato);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const nt = user?.tenant || {};
    setForm({
      smtp_host: nt.smtp_host || '', smtp_port: nt.smtp_port || 587,
      smtp_user: nt.smtp_user || '', smtp_password: '', smtp_use_tls: nt.smtp_use_tls ?? true,
    });
    setConfigured(!!nt.smtp_configurato);
  }, [user?.tenant]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const salva = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg(null);
    try {
      // smtp_password vuota → non sovrascrive quella salvata (write-only pattern)
      const payload = { ...form };
      if (!payload.smtp_password) delete payload.smtp_password;
      const { data } = await authApi.updateTenant(payload);
      setConfigured(!!data.smtp_configurato);
      setForm((f) => ({ ...f, smtp_password: '' }));
      onUpdate({ tenant: { ...(user?.tenant || {}), ...data } });
      setMsg({ type: 'success', text: 'Configurazione email salvata' });
    } catch (err) {
      const errData = err.response?.data;
      const text = (typeof errData === 'object' && errData !== null)
        ? Object.values(errData).flat().join(' ')
        : 'Errore nel salvataggio della configurazione email';
      setMsg({ type: 'error', text });
    } finally { setLoading(false); }
  };

  const testa = async () => {
    setTesting(true);
    setMsg(null);
    try {
      const payload = { ...form };
      if (!payload.smtp_password) delete payload.smtp_password;
      const { data } = await authApi.testSmtp(payload);
      setMsg({ type: 'success', text: data.message || 'Connessione SMTP riuscita' });
    } catch (err) {
      const text = err.response?.data?.error || 'Connessione SMTP fallita';
      setMsg({ type: 'error', text });
    } finally { setTesting(false); }
  };

  return (
    <Section title="Configurazione email" description="Invia documenti e comunicazioni dalla tua casella email (opzionale). Se non configurata, mAIstro usa il proprio mittente.">
      <Alert type={msg?.type} msg={msg?.text} onClose={() => setMsg(null)} />
      <form onSubmit={salva}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
          <Field label="SMTP Host">
            <input style={inp} value={form.smtp_host} onChange={(e) => set('smtp_host', e.target.value)} placeholder="smtp.gmail.com" maxLength={100} />
          </Field>
          <Field label="Porta">
            <input style={inp} type="number" value={form.smtp_port} onChange={(e) => set('smtp_port', e.target.value)} placeholder="587" />
          </Field>
        </div>
        <Field label="Utente SMTP">
          <input style={inp} type="email" value={form.smtp_user} onChange={(e) => set('smtp_user', e.target.value)} placeholder="mario@gmail.com" maxLength={150} />
        </Field>
        <Field label="Password SMTP">
          <PasswordInput value={form.smtp_password} onChange={(e) => set('smtp_password', e.target.value)} autoComplete="off" required={false} />
          {configured && !form.smtp_password && (
            <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 4 }}>Password già configurata (••••••••). Lascia vuoto per non modificarla.</div>
          )}
        </Field>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--ds-border)', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ds-text)' }}>Usa TLS</div>
            <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>Cifratura della connessione (consigliato per la porta 587).</div>
          </div>
          <button type="button" onClick={() => set('smtp_use_tls', !form.smtp_use_tls)} style={{
            width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
            background: form.smtp_use_tls ? 'var(--brand)' : 'var(--ds-border)',
            position: 'relative', transition: 'background 0.2s', flexShrink: 0,
          }}>
            <span style={{ position: 'absolute', top: 3, left: form.smtp_use_tls ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <SaveBtn loading={loading} label="Salva configurazione" />
          <button type="button" onClick={testa} disabled={testing} style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: '1px solid var(--brand)', background: 'transparent', color: 'var(--brand)', cursor: testing ? 'wait' : 'pointer', fontSize: 14, fontWeight: 500 }}>
            {testing ? 'Verifica…' : 'Testa connessione'}
          </button>
        </div>
      </form>
    </Section>
  );
}

// ── Password helpers ─────────────────────────────────────────────────────
const EyeIcon = ({ show }) => show ? <Eye size={16} strokeWidth={2} color="var(--ds-muted)" /> : <EyeOff size={16} strokeWidth={2} color="var(--ds-muted)" />;

function PasswordInput({ value, onChange, autoComplete, required = true }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} style={inp} value={value} onChange={onChange} required={required} autoComplete={autoComplete} />
      <button type="button" onClick={() => setShow(!show)} style={{
        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
        background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex',
      }}><EyeIcon show={show} /></button>
    </div>
  );
}

function getPasswordStrength(pw) {
  if (!pw) return { level: 0, label: '', color: 'transparent' };
  let criteria = 0;
  if (pw.length >= 12) criteria++;
  if (/[A-Z]/.test(pw)) criteria++;
  if (/[0-9]/.test(pw)) criteria++;
  if (/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(pw)) criteria++;
  if (criteria < 3) return { level: 1, label: 'Debole', color: 'var(--ds-danger)' };
  if (criteria === 3) return { level: 2, label: 'Discreta', color: 'var(--warn)' };
  if (pw.length <= 15) return { level: 3, label: 'Buona', color: 'var(--brand)' };
  return { level: 4, label: 'Forte', color: 'var(--ok)' };
}

function StrengthBar({ password }) {
  const s = getPasswordStrength(password);
  if (!password) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 3, marginBottom: 3 }}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: i <= s.level ? s.color : 'var(--ds-border)',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>
      <div style={{ fontSize: 10, color: s.color, fontWeight: 500 }}>{s.label}</div>
    </div>
  );
}

// ── Sezione Password ───────────────────────────────────────────────────────
function PasswordSection() {
  const [form, setForm] = useState({ vecchia_password: '', nuova_password: '', conferma: '' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const salva = async (e) => {
    e.preventDefault();
    if (form.nuova_password !== form.conferma) {
      setMsg({ type: 'error', text: 'Le due password non coincidono' });
      return;
    }
    const s = getPasswordStrength(form.nuova_password);
    if (s.level < 3) {
      setMsg({ type: 'error', text: 'La password deve avere almeno 12 caratteri, 1 maiuscola, 1 numero e 1 carattere speciale.' });
      return;
    }
    setLoading(true);
    try {
      await authApi.changePassword({ vecchia_password: form.vecchia_password, nuova_password: form.nuova_password });
      setMsg({ type: 'success', text: 'Password aggiornata con successo' });
      setForm({ vecchia_password: '', nuova_password: '', conferma: '' });
    } catch (err) {
      const errData = err.response?.data?.error;
      const text = Array.isArray(errData) ? errData.join(' ') : errData || 'Errore nel cambio password';
      setMsg({ type: 'error', text });
    } finally { setLoading(false); }
  };

  return (
    <Section title="Sicurezza" description="Cambia la tua password di accesso.">
      <Alert type={msg?.type} msg={msg?.text} onClose={() => setMsg(null)} />
      <form onSubmit={salva}>
        <Field label="Password attuale">
          <PasswordInput value={form.vecchia_password} onChange={(e) => set('vecchia_password', e.target.value)} autoComplete="current-password" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Nuova password">
            <PasswordInput value={form.nuova_password} onChange={(e) => set('nuova_password', e.target.value)} autoComplete="new-password" />
            <StrengthBar password={form.nuova_password} />
          </Field>
          <Field label="Conferma nuova password">
            <PasswordInput value={form.conferma} onChange={(e) => set('conferma', e.target.value)} autoComplete="new-password" />
          </Field>
        </div>
        <SaveBtn loading={loading} label="Aggiorna password" />
      </form>
    </Section>
  );
}

// ── Sezione Consensi ───────────────────────────────────────────────────────
function ConsensiSection({ user, onUpdate }) {
  const [marketing, setMarketing] = useState(user?.consenso_marketing ?? false);
  const [analytics, setAnalytics] = useState(user?.consenso_analytics ?? false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const salva = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.updateMe({ consenso_marketing: marketing, consenso_analytics: analytics });
      onUpdate({ consenso_marketing: marketing, consenso_analytics: analytics });
      setMsg({ type: 'success', text: 'Consensi aggiornati' });
    } catch {
      setMsg({ type: 'error', text: 'Errore nel salvataggio' });
    } finally { setLoading(false); }
  };

  const Toggle = ({ value, onChange, label, description }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--ds-border)' }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ds-text)' }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>{description}</div>
      </div>
      <button type="button" onClick={() => onChange(!value)} style={{
        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
        background: value ? 'var(--brand)' : 'var(--ds-border)',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      }}>
        <span style={{
          position: 'absolute', top: 3, left: value ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </button>
    </div>
  );

  return (
    <Section title="Privacy e consensi" description="Gestisci le tue preferenze di comunicazione e analisi.">
      <Alert type={msg?.type} msg={msg?.text} onClose={() => setMsg(null)} />
      <form onSubmit={salva}>
        <Toggle value={marketing} onChange={setMarketing} label="Comunicazioni marketing" description="Ricevi aggiornamenti su novità, offerte e consigli per la tua attività." />
        <Toggle value={analytics} onChange={setAnalytics} label="Analisi e miglioramento del prodotto" description="Ci aiuti a migliorare mAIstro condividendo dati anonimi sull'utilizzo." />
        <div style={{ marginTop: 20 }}>
          <SaveBtn loading={loading} label="Salva preferenze" />
        </div>
      </form>
    </Section>
  );
}

// ── Sezione Piano ─────────────────────────────────────────────────────────
function PianoSection({ piano, stripeToast, setStripeToast }) {
  // CAP-007: i prezzi dei piani a pagamento arrivano dal catalogo Stripe (endpoint),
  // NON piu' scritti a mano qui. Free/Trial restano statici (non hanno un price).
  const PIANI = {
    free:     { label: 'Piano Free',       color: 'var(--ds-muted)', prezzo: 'Gratis' },
    smart:    { label: 'Piano Smart',      color: 'var(--zone-blue)', prezzo: null },
    business: { label: 'Piano Business',   color: 'var(--ok)', prezzo: null },
    pro:      { label: 'Piano Pro',        color: 'var(--zone-violet)', prezzo: null },
    trial:    { label: 'Periodo di prova', color: 'var(--zone-amber)', prezzo: 'Gratuito' },
  };
  const STATI_COLOR = {
    attivo:     { bg: tint('var(--ok)', 12), color: 'var(--ok)', label: 'Attivo' },
    trial:      { bg: tint('var(--zone-amber)', 12), color: 'var(--zone-amber)', label: 'Trial' },
    sospeso:    { bg: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', label: 'Sospeso' },
    cancellato: { bg: 'var(--chip)', color: 'var(--ds-muted)', label: 'Cancellato' },
    scaduto:    { bg: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', label: 'Scaduto' },
  };

  const p = PIANI[piano] || PIANI.free;
  const [loading, setLoading] = useState(null);
  const [sub, setSub] = useState(null);

  // CAP-007 — listino dal catalogo Stripe. Tre stati: caricamento, errore, pronto.
  const [prezzi, setPrezzi] = useState(null);
  const [prezziLoading, setPrezziLoading] = useState(true);
  const [prezziError, setPrezziError] = useState(false);

  useEffect(() => {
    billingApi.me().then(r => setSub(r.data)).catch(() => {});
  }, []);

  // Estratta cosi' il bottone "Riprova" dello stato errore puo' ri-eseguirla
  // senza reload di pagina (torna a Caricamento → Pronto se il catalogo risponde).
  const caricaPrezzi = useCallback(() => {
    setPrezziLoading(true);
    setPrezziError(false);
    stripeApi.prezzi()
      .then(r => { setPrezzi(r.data); setPrezziError(false); })
      .catch(() => setPrezziError(true))
      .finally(() => setPrezziLoading(false));
  }, []);

  useEffect(() => { caricaPrezzi(); }, [caricaPrezzi]);

  // Prezzo MENSILE di un piano a pagamento, formattato per il design system.
  // Ritorna null se il listino non contiene il piano (bottone/prezzo NON mostrato).
  const prezzoMensile = (pianoKey) => {
    const m = prezzi && prezzi[pianoKey] && prezzi[pianoKey].month;
    if (!m) return null;
    const n = parseFloat(m.importo);
    if (Number.isNaN(n)) return null;
    const importo = Number.isInteger(n) ? String(n) : n.toFixed(2);
    return `€ ${importo}/mese`;
  };

  // Etichetta prezzo del piano corrente nella card: free/trial statici, a pagamento
  // dal catalogo ('…' mentre carica, '—' se indisponibile → mai un prezzo sbagliato).
  const prezzoCorrente = p.prezzo != null
    ? p.prezzo
    : (prezziLoading ? '…' : (prezzoMensile(piano) || '—'));

  const redirectToCheckout = async (targetPiano) => {
    setLoading(`upgrade_${targetPiano}`);
    try {
      const r = await api.post('/stripe/create-checkout-session/', { piano: targetPiano });
      window.location.href = r.data.url;
    } catch (e) {
      setStripeToast({ type: 'error', msg: e?.response?.data?.error || 'Errore nella creazione del pagamento.' });
      setLoading(null);
    }
  };

  const redirectToPortal = async () => {
    setLoading('portal');
    try {
      const r = await api.post('/stripe/portal/');
      window.location.href = r.data.url;
    } catch (e) {
      setStripeToast({ type: 'error', msg: e?.response?.data?.error || 'Errore nell\'apertura del portale.' });
      setLoading(null);
    }
  };

  const btnBase = { padding: '10px 20px', borderRadius: 'var(--radius-base)', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500 };

  const statoInfo = sub ? (STATI_COLOR[sub.stato] || STATI_COLOR.trial) : null;

  const fmtData = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const UsageBar = ({ label, used, max, unit = '' }) => {
    const pct = max === 0 ? 0 : Math.min(100, Math.round((used / max) * 100));
    const color = pct > 85 ? 'var(--ds-danger)' : pct > 60 ? 'var(--zone-amber)' : 'var(--ok)';
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ds-muted)', marginBottom: 5 }}>
          <span>{label}</span>
          <span style={{ fontWeight: 500, color: 'var(--ds-text)' }}>
            {max === 0 ? `${used}${unit} — illimitato` : `${used}${unit} / ${max}${unit}`}
          </span>
        </div>
        {max > 0 && (
          <div style={{ height: 6, background: 'var(--chip)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s' }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <Section id="piano" title="Piano e abbonamento" description="Il tuo piano corrente, utilizzi e storico pagamenti.">
      {stripeToast && (
        <Alert type={stripeToast.type} msg={stripeToast.msg} onClose={() => setStripeToast(null)} />
      )}

      {/* Card piano attuale */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 20px', background: 'var(--chip)', borderRadius: 'var(--radius-card)', marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Star size={20} color="#fff" strokeWidth={2} /></div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--ds-text)' }}>{p.label}</div>
            <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginTop: 1 }}>{prezzoCorrente}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {statoInfo && (
            <span style={{ fontSize: 12, fontWeight: 500, padding: '4px 12px', borderRadius: 100, background: statoInfo.bg, color: statoInfo.color }}>
              {statoInfo.label}
            </span>
          )}
          {sub?.data_rinnovo && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Prossimo rinnovo</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ds-text)' }}>{fmtData(sub.data_rinnovo)}</div>
            </div>
          )}
          {sub?.is_trial_attivo && sub?.trial_end && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Fine trial</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--zone-amber)' }}>{fmtData(sub.trial_end)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Utilizzi */}
      {sub?.utilizzi && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>Utilizzi del mese</div>
          <UsageBar label="Messaggi vocali AI" used={sub.utilizzi.messaggi_vocali} max={sub.limiti.messaggi_vocali} />
          <UsageBar label="Utenti attivi" used={sub.utilizzi.utenti} max={sub.limiti.utenti} />
          {sub.limiti.cantieri > 0 && <UsageBar label="Cantieri" used={sub.utilizzi.cantieri} max={sub.limiti.cantieri} />}
          <UsageBar label="Storage" used={sub.utilizzi.storage_gb} max={sub.limiti.storage_gb} unit=" GB" />
        </div>
      )}

      {/* Bottoni azione — i prezzi di upgrade arrivano dal catalogo (CAP-007), con
          i tre stati obbligatori: caricamento, errore, pronto. Mai un bottone con
          prezzo vuoto: se un piano non ha prezzo, il suo bottone non compare. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: sub?.transazioni?.length ? 24 : 0 }}>
        {/* Stato: caricamento prezzi */}
        {piano !== 'pro' && prezziLoading && (
          <button disabled aria-busy="true"
            style={{ ...btnBase, background: 'var(--chip)', color: 'var(--ds-muted)', cursor: 'default' }}>
            Caricamento prezzi…
          </button>
        )}

        {/* Stato: errore/indisponibile — messaggio leggibile + Riprova (rifà la fetch,
            nessun reload). Nessun bottone di upgrade con prezzo vuoto. */}
        {piano !== 'pro' && !prezziLoading && prezziError && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: 'var(--ds-text)' }}>
              Non riusciamo a caricare i piani in questo momento. Riprova tra poco.
            </span>
            <button onClick={caricaPrezzi}
              style={{ ...btnBase, background: 'var(--brand)', color: '#fff' }}>
              Riprova
            </button>
          </div>
        )}

        {/* Stato: pronto — bottoni con prezzo dal catalogo */}
        {!prezziLoading && !prezziError && piano === 'free' && prezzoMensile('smart') && (
          <button onClick={() => redirectToCheckout('smart')} disabled={!!loading}
            style={{ ...btnBase, background: 'var(--brand)', color: '#fff' }}>
            {loading === 'upgrade_smart' ? 'Reindirizzamento…' : `Upgrade a Smart — ${prezzoMensile('smart')}`}
          </button>
        )}
        {!prezziLoading && !prezziError && (piano === 'free' || piano === 'smart') && prezzoMensile('business') && (
          <button onClick={() => redirectToCheckout('business')} disabled={!!loading}
            style={{ ...btnBase, background: 'var(--brand)', color: '#fff' }}>
            {loading === 'upgrade_business' ? 'Reindirizzamento…' : `Upgrade a Business — ${prezzoMensile('business')}`}
          </button>
        )}
        {!prezziLoading && !prezziError && piano !== 'pro' && prezzoMensile('pro') && (
          <button onClick={() => redirectToCheckout('pro')} disabled={!!loading}
            style={{ ...btnBase, background: 'var(--brand)', color: '#fff' }}>
            {loading === 'upgrade_pro' ? 'Reindirizzamento…' : `Upgrade a Pro — ${prezzoMensile('pro')}`}
          </button>
        )}

        {/* Portale abbonamento — indipendente dai prezzi, sempre visibile a chi paga */}
        {(piano === 'smart' || piano === 'business' || piano === 'pro') && (
          <button onClick={redirectToPortal} disabled={!!loading}
            style={{ ...btnBase, background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', color: 'var(--ds-text)' }}>
            {loading === 'portal' ? 'Reindirizzamento…' : 'Gestisci abbonamento'}
          </button>
        )}
      </div>

      {/* Ultime transazioni */}
      {sub?.transazioni?.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Ultimi pagamenti</div>
          {sub.transazioni.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--ds-border)', fontSize: 13 }}>
              <div>
                <div style={{ fontWeight: 500, color: 'var(--ds-text)' }}>{t.descrizione}</div>
                <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>{fmtData(t.data)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 500, color: 'var(--ds-text)' }}>€ {t.importo.toFixed(2)}</span>
                <span style={{
                  fontSize: 11, fontWeight: 500, padding: '3px 9px', borderRadius: 100,
                  background: t.stato === 'completato' ? tint('var(--ok)', 12) : tint('var(--ds-danger)', 10),
                  color: t.stato === 'completato' ? 'var(--ok)' : 'var(--ds-danger)',
                }}>{t.stato}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── Sezione Sicurezza ─────────────────────────────────────────────────────
function SicurezzaSection({ user, onUpdate }) {
  const [mfaEnabled, setMfaEnabled] = useState(user?.mfa_enabled || false);
  const [toggling, setToggling] = useState(false);
  const [sessioni, setSessioni] = useState([]);
  const [loadingSessioni, setLoadingSessioni] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    setMfaEnabled(user?.mfa_enabled || false);
  }, [user]);

  useEffect(() => {
    authApi.sessions()
      .then(r => setSessioni(r.data))
      .catch(() => {})
      .finally(() => setLoadingSessioni(false));
  }, []);

  const toggleMfa = async () => {
    setToggling(true);
    try {
      const r = await authApi.toggleMfa(!mfaEnabled);
      setMfaEnabled(r.data.mfa_enabled);
      setMsg({ type: 'success', text: r.data.messaggio });
      onUpdate({ mfa_enabled: r.data.mfa_enabled });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.errore || 'Errore' });
    } finally {
      setToggling(false);
    }
  };

  const logoutAll = async () => {
    if (!window.confirm('Vuoi disconnettere tutti i dispositivi? Dovrai rieffettuare l\'accesso.')) return;
    setLogoutLoading(true);
    try {
      const r = await authApi.logoutAll();
      setMsg({ type: 'success', text: r.data.messaggio });
      setSessioni([]);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.errore || 'Errore' });
    } finally {
      setLogoutLoading(false);
    }
  };

  const fmtData = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Section title="Sicurezza" description="Proteggi il tuo account con verifica in 2 passaggi e gestisci le sessioni attive.">
      <Alert type={msg?.type} msg={msg?.text} onClose={() => setMsg(null)} />

      {/* MFA Toggle */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ds-text)', marginBottom: 4 }}>
          Verifica in 2 passaggi (Magic Link)
        </div>
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginBottom: 12 }}>
          Quando attiva, dopo il login riceverai un link di conferma via email prima di accedere.
        </div>
        <button
          onClick={toggleMfa}
          disabled={toggling}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 20px', borderRadius: 'var(--radius-base)',
            border: '1px solid ' + (mfaEnabled ? 'var(--brand)' : 'var(--ds-border)'),
            background: mfaEnabled ? tint('var(--brand)', 8) : 'var(--ds-bg)',
            color: mfaEnabled ? 'var(--brand)' : 'var(--ds-text)',
            fontSize: 13, fontWeight: 500, cursor: toggling ? 'wait' : 'pointer',
          }}
        >
          <span style={{
            width: 36, height: 20, borderRadius: 10,
            background: mfaEnabled ? 'var(--brand)' : 'var(--ds-border)',
            position: 'relative', display: 'inline-block', transition: 'background 0.2s',
          }}>
            <span style={{
              width: 16, height: 16, borderRadius: '50%', background: '#fff',
              position: 'absolute', top: 2,
              left: mfaEnabled ? 18 : 2,
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            }} />
          </span>
          {mfaEnabled ? 'Attiva' : 'Disattivata'}
        </button>
      </div>

      {/* Sessioni attive */}
      <div style={{ borderTop: '1px solid var(--ds-border)', paddingTop: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ds-text)', marginBottom: 12 }}>
          Sessioni attive
        </div>

        {loadingSessioni ? (
          <div style={{ height: 60, background: 'var(--ds-border)', borderRadius: 'var(--radius-base)' }} className="skeleton" />
        ) : sessioni.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--ds-muted)', padding: '12px 0' }}>
            Nessuna sessione attiva trovata.
          </div>
        ) : (
          <div style={{ borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', overflow: 'hidden', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--chip)' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>Sessione</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>Creata</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>Scadenza</th>
                  <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)' }}></th>
                </tr>
              </thead>
              <tbody>
                {sessioni.map(s => (
                  <tr key={s.id} style={{ borderTop: '1px solid var(--ds-border)' }}>
                    <td style={{ padding: '10px 12px', color: 'var(--ds-text)' }}>
                      {s.jti.slice(0, 8)}...
                      {s.is_current && (
                        <span style={{
                          marginLeft: 8, padding: '2px 8px', borderRadius: 10,
                          background: tint('var(--brand)', 12), color: 'var(--brand)',
                          fontSize: 10, fontWeight: 500,
                        }}>
                          corrente
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--ds-muted)' }}>{fmtData(s.created_at)}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--ds-muted)' }}>{fmtData(s.expires_at)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {s.is_current && <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button
          onClick={logoutAll}
          disabled={logoutLoading || sessioni.length === 0}
          style={{
            padding: '10px 20px', borderRadius: 'var(--radius-base)',
            border: '1px solid var(--ds-danger)', background: 'var(--ds-bg)',
            color: 'var(--ds-danger)', fontSize: 13, fontWeight: 500,
            cursor: logoutLoading ? 'wait' : 'pointer',
            opacity: sessioni.length === 0 ? 0.5 : 1,
          }}
        >
          {logoutLoading ? 'Disconnessione...' : 'Esci da tutti i dispositivi'}
        </button>
      </div>
    </Section>
  );
}

// ── Sezione GDPR ──────────────────────────────────────────────────────────
function GDPRSection({ onLogout }) {
  const [showDelete, setShowDelete] = useState(false);
  const [conferma, setConferma] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const esporta = async () => {
    try {
      const r = await authApi.gdprExport();
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'maistro_dati_personali.json'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Errore nell\'esportazione'); }
  };

  const elimina = async () => {
    if (conferma !== 'ELIMINA') { setMsg({ type: 'error', text: 'Scrivi ELIMINA per confermare' }); return; }
    setLoading(true);
    try {
      await authApi.gdprDelete({ conferma: 'ELIMINA' });
      onLogout();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Errore' });
      setLoading(false);
    }
  };

  return (
    <Section title="Privacy e dati personali (GDPR)" description="Esporta o elimina i tuoi dati personali in conformità al GDPR.">
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button onClick={esporta} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: 'var(--ds-text)' }}>
          <Download size={16} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Esporta i miei dati
        </button>
      </div>

      <div style={{ padding: '16px 20px', background: tint('var(--ds-danger)', 8), borderRadius: 'var(--radius-card)', border: '1px solid ' + tint('var(--ds-danger)', 30) }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ds-danger)', marginBottom: 4 }}>Zona pericolosa</div>
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginBottom: 12 }}>L'eliminazione dell'account è irreversibile. Tutti i tuoi dati verranno anonimizzati.</div>
        {!showDelete ? (
          <button onClick={() => setShowDelete(true)} style={{ padding: '8px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-danger)', background: 'var(--ds-bg)', color: 'var(--ds-danger)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            Elimina account
          </button>
        ) : (
          <div>
            <Alert type={msg?.type} msg={msg?.text} onClose={() => setMsg(null)} />
            <div style={{ fontSize: 13, color: 'var(--ds-danger)', marginBottom: 8 }}>Scrivi <b>ELIMINA</b> per confermare:</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={conferma} onChange={(e) => setConferma(e.target.value)}
                style={{ flex: 1, padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid ' + tint('var(--ds-danger)', 30), fontSize: 14, outline: 'none', fontFamily: 'inherit' }} placeholder="ELIMINA" />
              <button onClick={() => setShowDelete(false)} style={{ padding: '9px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 13 }}>Annulla</button>
              <button onClick={elimina} disabled={loading} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--ds-danger)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                {loading ? '…' : 'Conferma eliminazione'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

// D6 = A (01/11): nell'app non c'è la barra dell'indirizzo e il registratore di
// prova non ha voce di menu. Solo per il superamministratore e solo nell'app;
// stesso componente e stesso stile delle sezioni accanto (E143).
function StrumentiProvaSection() {
  return (
    <Section title="Strumenti di prova" description="Solo per il superamministratore, dentro l'app.">
      <Link to="/prova/registratore" data-testid="voce-registratore-prova" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, boxSizing: 'border-box', padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', fontSize: 14, fontWeight: 500, color: 'var(--ds-text)', textDecoration: 'none' }}>
        Registratore di prova
      </Link>
    </Section>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────
export default function Impostazioni() {
  const { user, piano, logout, loadMe } = useAuth();
  const [localUser, setLocalUser] = useState(user);
  const [stripeToast, setStripeToast] = useState(null);

  // Gestisce i query params al ritorno da Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const esito = params.get('stripe');
    if (esito === 'success') {
      setStripeToast({ type: 'success', msg: 'Abbonamento attivato con successo!' });
      loadMe();
    } else if (esito === 'cancel') {
      setStripeToast({ type: 'error', msg: 'Pagamento annullato.' });
    }
    if (esito) {
      // Rimuove il query param dall'URL senza ricaricare la pagina
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => { setLocalUser(user); }, [user]);

  // CAP-018 — «Passa al piano a pagamento» (modale limite cantieri) arriva su
  // /impostazioni#piano: si porta la sezione dell'abbonamento in vista.
  useEffect(() => {
    if (window.location.hash === '#piano') {
      document.getElementById('piano')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const aggiorna = (patch) => {
    setLocalUser((u) => ({ ...u, ...patch }));
    loadMe();
  };

  return (
    <div style={{ padding: '28px 20px 28px 32px', width: '100%', boxSizing: 'border-box', maxWidth: 800 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>Impostazioni</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ds-muted)' }}>Gestisci il tuo profilo e le preferenze dell'account</p>
      </div>

      <ProfiloSection user={localUser} onUpdate={aggiorna} />
      <DatiAziendaSection user={localUser} onUpdate={aggiorna} />
      <ConfigurazioneEmailSection user={localUser} onUpdate={aggiorna} />
      <PasswordSection />
      <SicurezzaSection user={localUser} onUpdate={aggiorna} />
      <ConsensiSection user={localUser} onUpdate={aggiorna} />
      <PianoSection piano={piano} stripeToast={stripeToast} setStripeToast={setStripeToast} />
      <GDPRSection onLogout={logout} />
      {voceRegistratoreVisibile(user, eAppNativa()) && <StrumentiProvaSection />}
    </div>
  );
}
